/**
 * Services API pour toutes les entités
 */

import apiClient from '../config/api.config';
import {
  Etudiant,
  UniteEnseignement,
  Matiere,
  Enseignant,
  NoteDevoir,
  NoteExamen,
  ResultatECUE,
  ResultatUE,
  ResultatSemestre,
  ReleveNotes,
  Parametre,
  DemandeRachat,
  Utilisateur,
  PaginatedResponse,
  LoginCredentials,
  AuthTokens,
  StatistiquesDashboard,
} from '../types';

// ========== AUTHENTIFICATION ==========

export const authService = {
  login: async (credentials: LoginCredentials): Promise<AuthTokens> => {
    const response = await apiClient.post('/auth/login/', credentials);
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('auth_token');
    window.location.href = '/login';
  },

  getCurrentUser: async (): Promise<Utilisateur> => {
    const response = await apiClient.get('/utilisateurs/me/');
    return response.data;
  },
};

// ========== ÉTUDIANTS ==========

export const etudiantService = {
  getAll: async (params?: Record<string, any>): Promise<PaginatedResponse<Etudiant>> => {
    const response = await apiClient.get('/etudiants/', { params });
    return response.data;
  },

  getById: async (id: number): Promise<Etudiant> => {
    const response = await apiClient.get(`/etudiants/${id}/`);
    return response.data;
  },

  getByMatricule: async (matricule: string): Promise<Etudiant> => {
    const response = await apiClient.get(`/etudiants/par-matricule/${matricule}/`);
    return response.data;
  },

  create: async (data: Partial<Etudiant> | FormData): Promise<Etudiant> => {
    const headers: Record<string, string> = {};
    if (!(data instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }
    const response = await apiClient.post('/etudiants/', data, { headers });
    return response.data;
  },

  update: async (id: number, data: Partial<Etudiant> | FormData): Promise<Etudiant> => {
    const headers: Record<string, string> = {};
    if (!(data instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }
    const response = await apiClient.patch(`/etudiants/${id}/`, data, { headers });
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/etudiants/${id}/`);
  },

  search: async (query: string): Promise<Etudiant[]> => {
    const response = await apiClient.get(`/etudiants/search/?q=${query}`);
    return response.data;
  },
};

// ========== UNITÉS D'ENSEIGNEMENT ==========

export const ueService = {
  getAll: async (params?: Record<string, any>): Promise<PaginatedResponse<UniteEnseignement>> => {
    const response = await apiClient.get('/ues/', { params });
    return response.data;
  },

  getById: async (id: number): Promise<UniteEnseignement> => {
    const response = await apiClient.get(`/ues/${id}/`);
    return response.data;
  },

  getByNiveau: async (niveau: string): Promise<UniteEnseignement[]> => {
    const response = await apiClient.get(`/ues/par-niveau/${niveau}/`);
    return response.data;
  },

  create: async (data: Partial<UniteEnseignement>): Promise<UniteEnseignement> => {
    const response = await apiClient.post('/ues/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<UniteEnseignement>): Promise<UniteEnseignement> => {
    const response = await apiClient.put(`/ues/${id}/`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/ues/${id}/`);
  },
};

// ========== MATIÈRES ==========

export const matiereService = {
  getAll: async (params?: Record<string, any>): Promise<PaginatedResponse<Matiere>> => {
    const response = await apiClient.get('/matieres/', { params });
    return response.data;
  },

  getById: async (id: number): Promise<Matiere> => {
    const response = await apiClient.get(`/matieres/${id}/`);
    return response.data;
  },

  getByUE: async (ueId: number): Promise<Matiere[]> => {
    const response = await apiClient.get(`/matieres/par-ue/${ueId}/`);
    return response.data;
  },

  create: async (data: Partial<Matiere>): Promise<Matiere> => {
    const response = await apiClient.post('/matieres/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<Matiere>): Promise<Matiere> => {
    const response = await apiClient.put(`/matieres/${id}/`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/matieres/${id}/`);
  },
};

// ========== SAISIE NOTES PAR CODE COPIE (fix 2026-09-02) ==========

export interface CopieSaisieRow {
  code_copie: string;
  copie_id: number;
  note_existante: number | null;
  absent_existant: boolean;
  saisie: boolean;
  date_saisie: string | null;
  etudiant_matricule?: string;   // absent si anonymat_actif
  etudiant_nom?: string;
  // Fix 2026-09-07 : classe pour vérification visuelle (seulement si
  // anonymat désactivé). L2EM, L1MI, etc.
  etudiant_classe?: string;
  etudiant_photo?: string | null;
}

export interface CopiesPourSaisieResponse {
  matiere: {
    id: number; code: string; nom: string; niveau: string; option: string;
  };
  matiere_ids: number[];
  codes_matieres: string[];
  classes_concernees: string[];
  enseignants: string[];
  // Fix 2026-09-07 : formateur du groupe filtré
  enseignant_id?: number | null;
  enseignant_nom?: string;
  type_examen: 'NORMAL' | 'RATTRAPAGE';
  annee_academique: string;
  semestre: string;
  anonymat_actif: boolean;
  total: number;
  nb_saisies: number;
  nb_restantes: number;
  copies: CopieSaisieRow[];
}

export interface MatiereGroupeeItem {
  cle: string;
  nom_matiere: string;
  niveau: string;
  semestre_absolu: string;
  semestre_relatif: string;
  matiere_ids: number[];
  codes_matieres: string[];
  classes: string[];
  // Fix 2026-09-07 : pôles du groupe pour filtrage frontend
  poles?: string[];
  enseignants: string[];
  // Fix 2026-09-07 : formateur du groupe (2 formateurs sur la même matière
  // = 2 groupes distincts avec 2 relevés séparés)
  enseignant_id?: number | null;
  enseignant_nom?: string;
  nb_copies_total: number;
  nb_notes_saisies: number;
}

export interface HistoriqueSaisieItem {
  code_copie: string;
  matiere_id: number;
  note: number | null;
  absent: boolean;
  agent_id: number | null;
  agent_nom: string;
  agent_role: string;
  date: string | null;
  action: 'creation' | 'modification';
}

export const saisieParCodeService = {
  // Liste GROUPÉE des matières (regroupe les homonymes)
  listerMatieres: async (params: {
    type_examen?: 'NORMAL' | 'RATTRAPAGE';
    parite?: 'impair' | 'pair';
    annee_academique?: string;
  }): Promise<{ matieres: MatiereGroupeeItem[]; total: number }> => {
    const response = await apiClient.get('/copies-examens/matieres-pour-saisie-code/', { params });
    return response.data;
  },
  // Copies pour un groupe de matieres (matiere_ids = "1,2,3")
  // Fix 2026-09-07 : `enseignant_id` optionnel pour cibler UN formateur
  // précis quand plusieurs formateurs se partagent la même matière.
  liste: async (params: {
    matiere_id?: number; matiere_ids?: string;
    enseignant_id?: number | 'none';
    type_examen?: 'NORMAL' | 'RATTRAPAGE';
    annee_academique?: string; semestre?: string;
  }): Promise<CopiesPourSaisieResponse> => {
    const response = await apiClient.get('/copies-examens/pour-saisie-code/', { params });
    return response.data;
  },
  // Historique des saisies pour audit
  historique: async (params: {
    matiere_ids: string; type_examen?: 'NORMAL' | 'RATTRAPAGE'; limit?: number;
  }): Promise<{ total: number; items: HistoriqueSaisieItem[] }> => {
    const response = await apiClient.get('/copies-examens/historique-saisie-code/', { params });
    return response.data;
  },
  saisir: async (body: {
    code_copie: string; note?: number | null; absent?: boolean;
    type_examen?: 'NORMAL' | 'RATTRAPAGE';
    annee_academique?: string; semestre?: string;
  }): Promise<{
    success: boolean; code_copie: string;
    note: number | null; absent: boolean; created: boolean; etudiant_id: number;
  }> => {
    const response = await apiClient.post('/copies-examens/saisir-note-par-code/', body);
    return response.data;
  },
  getAnonymat: async (): Promise<{ actif: boolean }> => {
    const response = await apiClient.get('/parametres/anonymat-saisie-code/');
    return response.data;
  },
  setAnonymat: async (actif: boolean): Promise<{ actif: boolean }> => {
    const response = await apiClient.post('/parametres/anonymat-saisie-code/', { actif });
    return response.data;
  },
};

// ========== NOTES DEVOIRS ==========

export const noteDevoirService = {
  getAll: async (params?: Record<string, any>): Promise<PaginatedResponse<NoteDevoir>> => {
    const response = await apiClient.get('/notes-devoirs/', { params });
    return response.data;
  },

  getById: async (id: number): Promise<NoteDevoir> => {
    const response = await apiClient.get(`/notes-devoirs/${id}/`);
    return response.data;
  },

  getByEtudiant: async (etudiantId: number): Promise<NoteDevoir[]> => {
    const response = await apiClient.get(`/notes-devoirs/par-etudiant/${etudiantId}/`);
    return response.data;
  },

  create: async (data: Partial<NoteDevoir>): Promise<NoteDevoir> => {
    const response = await apiClient.post('/notes-devoirs/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<NoteDevoir>): Promise<NoteDevoir> => {
    const response = await apiClient.put(`/notes-devoirs/${id}/`, data);
    return response.data;
  },

  saisirBatch: async (notes: Partial<NoteDevoir>[]): Promise<{ created: number; updated: number }> => {
    const response = await apiClient.post('/notes-devoirs/saisir-batch/', { notes });
    return response.data;
  },
};

// ========== NOTES EXAMENS ==========

export const noteExamenService = {
  getAll: async (params?: Record<string, any>): Promise<PaginatedResponse<NoteExamen>> => {
    const response = await apiClient.get('/notes-examens/', { params });
    return response.data;
  },

  getById: async (id: number): Promise<NoteExamen> => {
    const response = await apiClient.get(`/notes-examens/${id}/`);
    return response.data;
  },

  getByEtudiant: async (etudiantId: number): Promise<NoteExamen[]> => {
    const response = await apiClient.get(`/notes-examens/par-etudiant/${etudiantId}/`);
    return response.data;
  },

  create: async (data: Partial<NoteExamen>): Promise<NoteExamen> => {
    const response = await apiClient.post('/notes-examens/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<NoteExamen>): Promise<NoteExamen> => {
    const response = await apiClient.put(`/notes-examens/${id}/`, data);
    return response.data;
  },

  saisirBatch: async (notes: Partial<NoteExamen>[]): Promise<{ created: number; updated: number }> => {
    const response = await apiClient.post('/notes-examens/saisir-batch/', { notes });
    return response.data;
  },
};

// ========== RÉSULTATS ==========

export const resultatService = {
  getResultatsEtudiant: async (matricule: string): Promise<{
    ecue: ResultatECUE[];
    ue: ResultatUE[];
    semestres: ResultatSemestre[];
  }> => {
    const response = await apiClient.get(`/resultats-ecue/par-etudiant/${matricule}/`);
    return response.data;
  },

  calculerEtudiant: async (matricule: string): Promise<any> => {
    const response = await apiClient.post('/resultats-ecue/calculer/', { matricule });
    return response.data;
  },

  calculerNiveau: async (niveau: string): Promise<any> => {
    const response = await apiClient.post('/resultats-ecue/calculer-niveau/', { niveau });
    return response.data;
  },

  calculerTous: async (): Promise<any> => {
    const response = await apiClient.post('/resultats-ecue/calculer-tous/');
    return response.data;
  },
};

// ========== RELEVÉS DE NOTES ==========

export const releveService = {
  getAll: async (params?: Record<string, any>): Promise<PaginatedResponse<ReleveNotes>> => {
    const response = await apiClient.get('/releves/', { params });
    return response.data;
  },

  getById: async (id: number): Promise<ReleveNotes> => {
    const response = await apiClient.get(`/releves/${id}/`);
    return response.data;
  },

  getByEtudiant: async (matricule: string): Promise<{
    etudiant: any;
    nombre_releves: number;
    releves: ReleveNotes[];
  }> => {
    const response = await apiClient.get(`/releves/par-etudiant/${matricule}/`);
    return response.data;
  },

  genererSemestriel: async (data: {
    matricule: string;
    semestre: number;
    annee_academique: string;
  }): Promise<ReleveNotes> => {
    const response = await apiClient.post('/releves/generer-semestriel/', data);
    return response.data.releve;
  },

  genererBatch: async (data: {
    matricules: string[];
    semestre: number;
    annee_academique: string;
  }): Promise<{
    total: number;
    generes: number;
    echecs: number;
    details: any;
  }> => {
    const response = await apiClient.post('/releves/generer-batch/', data);
    return response.data;
  },

  telecharger: async (id: number): Promise<Blob> => {
    const response = await apiClient.get(`/releves/${id}/telecharger/`, {
      responseType: 'blob',
    });
    return response.data;
  },

  verifier: async (code: string): Promise<{
    valide: boolean;
    etudiant?: any;
    type_releve?: string;
    semestre?: number;
    annee_academique?: string;
    date_generation?: string;
    genere_par?: string;
    message?: string;
  }> => {
    const response = await apiClient.get(`/releves/verifier/${code}/`);
    return response.data;
  },
};

// ========== PARAMÈTRES ==========

export const parametreService = {
  getAll: async (): Promise<Parametre[]> => {
    const response = await apiClient.get('/parametres/');
    return response.data.results || response.data;
  },

  getByKey: async (key: string): Promise<Parametre> => {
    const response = await apiClient.get(`/parametres/by-key/${key}/`);
    return response.data;
  },

  update: async (id: number, data: Partial<Parametre>): Promise<Parametre> => {
    const response = await apiClient.put(`/parametres/${id}/`, data);
    return response.data;
  },
};

// ========== RACHAT ==========

export const rachatService = {
  getAll: async (params?: Record<string, any>): Promise<PaginatedResponse<DemandeRachat>> => {
    const response = await apiClient.get('/demandes-rachat/', { params });
    return response.data;
  },

  getById: async (id: number): Promise<DemandeRachat> => {
    const response = await apiClient.get(`/demandes-rachat/${id}/`);
    return response.data;
  },

  creer: async (data: {
    etudiant_matricule: string;
    matiere_code: string;
    motif_demande: string;
  }): Promise<DemandeRachat> => {
    const response = await apiClient.post('/demandes-rachat/', data);
    return response.data;
  },

  approuver: async (id: number, nouvelle_note: string): Promise<DemandeRachat> => {
    const response = await apiClient.post(`/demandes-rachat/${id}/approuver/`, { nouvelle_note });
    return response.data;
  },

  rejeter: async (id: number, motif_rejet: string): Promise<DemandeRachat> => {
    const response = await apiClient.post(`/demandes-rachat/${id}/rejeter/`, { motif_rejet });
    return response.data;
  },
};

// ========== STATISTIQUES ==========

export const statistiquesService = {
  getDashboard: async (params: { annee_academique?: string; pole?: string; cycle?: 'licence' | 'master' } = {}): Promise<StatistiquesDashboard> => {
    const response = await apiClient.get('/parametres/statistiques_dashboard/', { params });
    return response.data;
  },

  getDetailees: async (params: {
    annee_academique?: string;
    niveau?: string;
    pole?: string;
    cycle?: 'licence' | 'master';
  } = {}): Promise<any> => {
    const response = await apiClient.get('/parametres/statistiques_detaillees/', { params });
    return response.data;
  },

  getNotesDashboardAlertes: async (params: {
    annee_academique?: string;
    semestre?: string;
    niveau?: string;
    pole?: string;
    limit?: number;
  } = {}): Promise<NotesDashboardAlertesResponse> => {
    const response = await apiClient.get('/parametres/notes_dashboard_alertes/', { params });
    return response.data;
  },

  // Fix 2026-09-02 : matières où les étudiants ont mal travaillé
  getMatieresCritiques: async (params: {
    annee_academique?: string;
    parite?: 'impair' | 'pair' | 'toutes';
    session?: 'ordinaire' | 'rattrapage' | 'toutes';
    niveau?: string;
    pole?: string;
    seuil_echec?: number;
    limit?: number;
  } = {}): Promise<{
    annee_academique: string;
    parite: string;
    session: string;
    seuil_echec: number;
    totaux: {
      nb_matieres: number;
      nb_critiques: number;
      nb_surveiller: number;
      moy_examen_globale: number | null;
    };
    matieres: Array<{
      matiere_id: number;
      code_matiere: string;
      nom_matiere: string;
      niveau: string;
      semestre: string;
      option: string;
      pole: string;
      nb_etudiants: number;
      nb_notes_devoir: number;
      moy_devoir: number | null;
      taux_echec_devoir: number | null;
      nb_notes_examen: number;
      moy_examen: number | null;
      taux_echec_examen: number | null;
      nb_notes_rattrapage: number;
      moy_rattrapage: number | null;
      taux_echec_rattrapage: number | null;
      criticite: 'critique' | 'surveiller' | 'ok';
      score_criticite: number;
    }>;
    repartition_pole: Array<{
      pole: string;
      nb_matieres: number;
      nb_critiques: number;
      moy_score: number;
    }>;
  }> => {
    const response = await apiClient.get('/parametres/matieres_critiques/', { params });
    return response.data;
  },

  previewScopeNotes: async (body: {
    scope: 'classes' | 'niveau' | 'parcours' | 'matiere';
    matiere_id: number;
    classes?: string[];
    niveau?: string;
    parcours_nom?: string;
  }): Promise<{
    scope: string;
    classes: string[];
    nb_classes: number;
    nb_etudiants: number;
  }> => {
    const response = await apiClient.post('/parametres/preview_scope_notes/', body);
    return response.data;
  },

  getAuditNotesOcr: async (params: { annee_academique?: string; limit?: number } = {}): Promise<AuditNotesOcrResponse> => {
    const response = await apiClient.get('/parametres/audit_notes_ocr/', { params });
    return response.data;
  },

  reparerAuditA1: async (body: { annee_academique?: string } = {}): Promise<{
    success: boolean; nb_notes_creees: number; nb_skipped: number;
    nb_recalculs: number; message: string;
  }> => {
    const response = await apiClient.post('/parametres/reparer_audit_a1/', body);
    return response.data;
  },

  // ─── Audit DEVOIRS (mirror examens) ─────────────────────────────────
  getAuditNotesDevoirs: async (params: { annee_academique?: string; limit?: number } = {}): Promise<AuditNotesDevoirsResponse> => {
    const response = await apiClient.get('/parametres/audit_notes_devoirs_ocr/', { params });
    return response.data;
  },

  reparerAuditDevoirsD1: async (body: { annee_academique?: string } = {}): Promise<{
    success: boolean; nb_notes_creees: number; nb_skipped: number;
    nb_recalculs: number; message: string;
  }> => {
    const response = await apiClient.post('/parametres/reparer_audit_devoirs_d1/', body);
    return response.data;
  },
};

// ─── Types audit OCR ───────────────────────────────────────────────────────
export interface AuditNotesOcrResponse {
  annee_academique: string;
  total_alertes_critiques: number;
  A1_notes_ocr_sans_db: {
    count: number;
    releves: Array<{
      releve_id: number; code_releve: string; status: string;
      matiere_nom: string | null; nb_manquantes: number;
    }>;
  };
  A2_lignes_orphelines: {
    count: number;
    releves: Array<{ releve_id: number; code_releve: string; status: string; nb: number }>;
  };
  A3_copies_notees_sans_db: { count: number };
  A4_drift_valeurs: {
    count: number;
    cas: Array<{
      ligne_id: number; code_releve: string; matricule: string; nom: string;
      classe: string; matiere_code: string; note_ocr: number; note_db: number; ecart: number;
    }>;
  };
  A5_releves_en_attente: {
    count: number;
    releves: Array<{
      releve_id: number; code_releve: string; status: string;
      date_scan: string | null; matiere_nom: string | null;
      matiere_code: string | null; nb_lignes: number;
    }>;
  };
}

// ─── Types audit DEVOIRS ────────────────────────────────────────────────────
export interface AuditNotesDevoirsResponse {
  annee_academique: string;
  total_alertes_critiques: number;
  D1_notes_ocr_sans_db: {
    count: number;
    releves: Array<{
      releve_id: number; code_releve: string; status: string;
      matiere_nom: string | null; nb_manquantes: number;
    }>;
  };
  D2_lignes_orphelines: {
    count: number;
    releves: Array<{ releve_id: number; code_releve: string; status: string; nb: number }>;
  };
  D3_drift_valeurs: {
    count: number;
    cas: Array<{
      ligne_id: number; code_releve: string; matricule: string; nom: string;
      classe: string; matiere_code: string;
      ocr_d1: number | null; db_d1: number | null;
      ocr_d2: number | null; db_d2: number | null;
    }>;
  };
  D4_releves_en_attente: {
    count: number;
    releves: Array<{
      releve_id: number; code_releve: string; status: string;
      date_scan: string | null; matiere_nom: string | null;
      matiere_code: string | null; nb_lignes: number;
    }>;
  };
}

// ─── Types pour les alertes notes dashboard ─────────────────────────────────
export interface MatiereSansNotes {
  matiere_id: number;
  matiere_code: string;
  matiere_nom: string;
  niveau: string;
  option: string;
  nb_etudiants_attendus: number;
  classes: string[];
}
export interface ClasseFaibleComplete {
  classe: string;
  niveau: string;
  pole: string;
  option: string;
  nb_matieres: number;
  nb_etudiants: number;
  pct_devoirs: number;
  pct_examens: number;
  pct_global: number;
}
export interface NotesDashboardAlertesResponse {
  annee_academique: string;
  semestre: string;
  stats: {
    total_matieres: number;
    nb_matieres_sans_devoirs: number;
    nb_matieres_sans_examens: number;
    nb_classes_faibles: number;
    pct_matieres_sans_devoirs: number;
    pct_matieres_sans_examens: number;
  };
  matieres_sans_devoirs: MatiereSansNotes[];
  matieres_sans_examens: MatiereSansNotes[];
  classes_completude_faible: ClasseFaibleComplete[];
}
